import cx from 'classnames';
import { ReactNode, useState, createContext, useContext } from 'react';
import React from 'react';

import styles from './OptionItem.module.css';

import OptionItemBase from '../OptionItemBase/OptionItemBase';
import Text from '../Text/Text';
import Loader from '../Loader';
import Center from '../Center/Center';
import Tooltip from '../Tooltip/Tooltip';
import Icon from '../Icon/Icon';

import { ReactComponent as ArrowRightSvg } from '@frontend/icons/arrow-right.svg';
import { ReactComponent as InfoSvg } from '@frontend/icons/info.svg';
import { useDismiss, useInteractions, useRole } from '@floating-ui/react';
import { useFocus } from '@floating-ui/react';
import { useHover } from '@floating-ui/react';
import { useFloating } from '@floating-ui/react';

interface TooltipContextType {
  open: boolean;
  showInfoTooltipOnHover?: boolean;
}

const TooltipContext = createContext<TooltipContextType>({
  open: false,
  showInfoTooltipOnHover: false,
});

const useTooltipContext = () => useContext(TooltipContext);

export interface OptionItemProps {
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  rightSlot?: ReactNode;
  name: string;
  description?: string;
  rightTopSlot?: ReactNode;
  rightBottomSlot?: ReactNode;
  showInfoTooltipOnHover?: boolean;
  dataTestId?: string;
}

export default function OptionItem({
  className,
  name,
  description,
  icon,
  loading,
  rightTopSlot,
  rightBottomSlot,
  disabled: disabledProp,
  showInfoTooltipOnHover,
  dataTestId,
  ...otherProps
}: OptionItemProps) {
  const disabled = disabledProp || loading;

  const Arrow = () => (
    <Icon
      data-test-id="currency-option-arrow-icon"
      color="neutral-500"
      size={24}
      component={<ArrowRightSvg />}
    />
  );

  const [open, setOpen] = useState(false);

  const getRightSlot = () => {
    if (loading) {
      return (
        <Center>
          <Loader color="neutral-500" size={24} />
        </Center>
      );
    }

    if (rightTopSlot || rightBottomSlot) {
      return (
        <TooltipContext.Provider value={{ open, showInfoTooltipOnHover }}>
          <div className={styles['right-slot']}>
            {rightTopSlot}
            {rightBottomSlot}
          </div>
        </TooltipContext.Provider>
      );
    }

    return (
      <Center>
        <Arrow />
      </Center>
    );
  };

  const leftSlot = <IconWrapper>{icon}</IconWrapper>;

  const middleSlot = (
    <div className={styles['middle-slot']}>
      <Name name={name} disabled={disabled} />
      {description && (
        <Description description={description} disabled={disabled} />
      )}
    </div>
  );

  const { refs, context } = useFloating({
    open,
    onOpenChange: (open) => {
      setOpen(open);
    },
  });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: 300,
      close: 300,
    },
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'tooltip',
  });

  const { getReferenceProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <div ref={refs.setReference} {...getReferenceProps()}>
      <OptionItemBase
        as="button"
        type="button"
        data-test-id={dataTestId ? dataTestId : 'option-item'}
        className={cx(
          styles['base'],
          { [styles['is-disabled']]: disabled },
          className
        )}
        innerClassName={styles['inner']}
        leftSlot={leftSlot}
        middleSlot={middleSlot}
        rightSlot={getRightSlot()}
        disabled={disabled}
        {...otherProps}
      />
    </div>
  );
}

function Name({ name, disabled }: { name: string; disabled?: boolean }) {
  return (
    <Text
      size="small"
      color={disabled ? 'neutral-400' : 'neutral-900'}
      weight="600"
      data-test-id="option-item-name"
    >
      {name}
    </Text>
  );
}

function IconWrapper({ children }: { children: ReactNode }) {
  return <Center className={styles['icon-wrapper']}>{children}</Center>;
}

function Description({
  description,
  disabled,
}: {
  description: string;
  disabled?: boolean;
}) {
  return (
    <Text
      size="tiny"
      color={disabled ? 'neutral-400' : 'neutral-500'}
      weight="400"
    >
      {description}
    </Text>
  );
}

export function InfoText({
  text,
  trailingSlot,
}: {
  text: string;
  trailingSlot?: ReactNode;
}) {
  return (
    <div className={styles['info-text']}>
      <Text size="tiny" color="neutral-900" weight="400">
        {text}
      </Text>
      {trailingSlot}
    </div>
  );
}

export function InfoTooltip({ tooltipContent }: { tooltipContent: ReactNode }) {
  const { open, showInfoTooltipOnHover } = useTooltipContext();

  return (
    <Tooltip
      open={showInfoTooltipOnHover ? open : undefined}
      content={tooltipContent}
      placement="top-end"
      wrapperClassName={styles['tooltip']}
    >
      <Icon color="neutral-500" size={16} component={<InfoSvg />} />
    </Tooltip>
  );
}
